import SectionHeroArchivePage from "components/SectionHeroArchivePage/SectionHeroArchivePage";
import { Order } from "containers/CheckOutPage/CheckOutPage";
import { FC, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { finalizeOrder } from "services/payment";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { resetCart } from "store/actions/cart";
import { resetSearch } from "store/actions/search";
import { euro } from "utils/currency";
import { useReactToPrint } from "react-to-print";
import GreeceImage from "images/orbito/ΟΤ_confirmation_Greece_Athens.jpg";
import moment from "moment";
import { useCustomSearchParams } from "context/SearchParamsContext";

export interface PayPageProps {
  className?: string;
}

const PayPage: FC<PayPageProps> = ({ className = "" }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const tawkApiRef = useRef(null);
  let componentRef = useRef<HTMLDivElement>(null);
  const { getStringParams, data } = useCustomSearchParams();
  const params = new URLSearchParams(location.search);
  const paymentIntent = params.get("payment_intent");
  const clientSecret = params.get("payment_intent_client_secret");
  const [order, setOrder] = useState<Order>();
  const [paymentMethod, setPaymentMethod] = useState<any>(); // New state for paymentMethod

  const datatemp = data();
  const destination =
    datatemp?.travelingTo?.city + ", " + datatemp?.travelingTo?.country || "";

  useEffect(() => {
    const finalizeOrderAsync = async () => {
      try {
        if (clientSecret && paymentIntent) {
          const { order, paymentMethod } = await finalizeOrder(
            clientSecret,
            paymentIntent
          );
          if (order) {
            setOrder(order);
            setPaymentMethod(paymentMethod); // Set the paymentMethod state
            resetState();
          } else {
            navigate("/");
          }
        } else {
          navigate("/");
        }
      } catch (error) {
        console.error("Error finalizing order:", error);
      }
    };

    finalizeOrderAsync();
  }, [clientSecret, paymentIntent]);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const resetState = () => {
    dispatch(resetCart());
    dispatch(resetSearch());
  };

  const { customers, dates, totalPrice, _id, status, orderId } = order || {}; // Destructure order

  const renderSidebar = () => {
    return (
      <div className="w-full flex flex-col sm:rounded-2xl lg:border border-neutral-200 dark:border-neutral-700 space-y-3 px-0 sm:p-6 xl:p-8 ">
        <div>
          <ButtonPrimary
            onClick={handlePrint}
            className="w-full bg-neutral-500 hover:bg-[#F47D20]"
          >
            Print this page
          </ButtonPrimary>
        </div>
        <div>
          <a
            href="mailto:subscriptions@orbito.travel"
            className="nc-Button relative h-auto inline-flex items-center justify-center rounded-full transition-colors text-sm sm:text-base font-medium px-4 py-3 sm:px-6  ttnc-ButtonPrimary disabled:bg-opacity-70
                  text-neutral-50 w-full 
                 bg-neutral-500 hover:bg-[#F47D20] focus:outline-none focus:ring-2 
                 focus:ring-offset-2 focus:ring-primary-6000 dark:focus:ring-offset-0"
          >
            Send us a message
          </a>
        </div>
      </div>
    );
  };

  const renderRowDetail = (title: string, value: string) => {
    return (
      <div className="flex flex-row ">
        <div className="flex-1 font-semibold">{title}</div>
        <div className="flex-1">{value}</div>
      </div>
    );
  };

  const renderMainSection = () => {
    return (
      <div
        ref={componentRef}
        className="w-full lg:w-3/5 xl:w-2/3 lg:pr-10 space-y-5"
      >
        <div className="w-full flex flex-col sm:rounded-2xl sm:border border-neutral-200 dark:border-neutral-700 space-y-8 px-0 sm:p-6 xl:p-8">
          <div>
            <p>{orderId}</p>
          </div>
          <img src={GreeceImage} className="" alt="Confirmation" />
          <div className="flex flex-col space-y-3">
            {Array.isArray(customers) ? (
              <>
                {customers.length === 1 &&
                  renderRowDetail(
                    "Main Traveler",
                    order!.customers[0]?.lastName
                  )}
                {customers.length > 1 &&
                  renderRowDetail(
                    "Additional Traveler",
                    order!.customers[0]?.lastName
                  )}
              </>
            ) : null}
            {renderRowDetail("Trip to", destination)}
            {dates &&
              renderRowDetail("Travel dates", dates.replace("-", "until"))}
            {totalPrice &&
              renderRowDetail("Total amount", euro.format(totalPrice))}
            {renderRowDetail("Payment Method", "Card")}
            {/* Display Card Type */}
            {order?.cardFunding &&
              renderRowDetail(
                "Card Type",
                order.cardFunding.charAt(0).toUpperCase() +
                  order.cardFunding.slice(1)
              )}
            {/* Alternatively, use paymentMethod details */}
            {paymentMethod &&
              renderRowDetail(
                "Card Details",
                `${paymentMethod.card.brand.toUpperCase()} ${
                  paymentMethod.card.funding.charAt(0).toUpperCase() +
                  paymentMethod.card.funding.slice(1)
                }`
              )}
            {status && renderRowDetail("Status", "Payment authorization")}
          </div>
        </div>
        <div className="w-full flex flex-col sm:rounded-2xl sm:border border-neutral-200 dark:border-neutral-700 space-y-8  px-0 sm:p-6 xl:p-8">
          <h2 className="text-lg font-black"> Thank you for your booking </h2>
          <div className="border-b border-neutral-200 dark:border-neutral-700 w-1/3"></div>
          <p>
            Your credit/debit card has been successfully authorized and your
            reservation is getting a final check to ensure a smooth and
            worry-free trip.
            <span className="block mt-5">
              This extra step ensures all your chosen dates, options and details
              are perfect before your final payment.
            </span>
          </p>
        </div>
        <div className="w-full flex flex-col sm:rounded-2xl sm:border border-neutral-200 dark:border-neutral-700 space-y-8 px-0 sm:p-6 xl:p-8">
          <h2 className="text-lg font-black"> What happens next</h2>
          <div className="border-b border-neutral-200 dark:border-neutral-700 w-1/3"></div>
          <p>
            Within 24 hours, you will receive a final confirmation email with
            all your official details, tickets, and essential trip information.
            <span className="block mt-5">
              Full payment will be processed when your booking is confirmed.
            </span>
          </p>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`nc-CheckOutPage  ${className} container`}
      data-nc-id="CheckOutPage"
    >
      <main className="mt-11 mb-24 lg:mb-32 flex flex-col-reverse lg:flex-row relative">
        {renderMainSection()}
        <div className="block flex-grow w-1/5">
          <div className="sticky top-52 ">{renderSidebar()}</div>
        </div>
      </main>
    </div>
  );
};

export default PayPage;
